import {
  FGCustomInput,
  FGCustomPanel,
  FGListen,
  FGSelectInput,
  FGTextInput,
} from "nsitools-react";
import * as React from "react";
import { FilePond } from "react-filepond";
import * as Yup from "yup";

import { DogApi, ERequestState } from "../../../../api";
import { useAuth, useGlobalData } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { DrupalStepperButtonBloc } from "../../../common";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";

export interface IDrupalTitleRequestFormProps {
  stepNumber: number;
  initialdata: any;
  onStepChange: (newStep: number) => void;
  save: (data?: any) => Promise<any> | any;
  loading: boolean;
  files: any[];
  setFiles: React.Dispatch<React.SetStateAction<any[]>>;
  showStepper?: boolean;
}

export const DrupalTitleRequestForm: React.FunctionComponent<IDrupalTitleRequestFormProps> = ({
  onStepChange,
  stepNumber,
  save,
  loading,
  initialdata,
  files,
  setFiles,
  showStepper = true,
}) => {
  const { user } = useAuth();
  const { t } = useTl();
  const dogApi = useApiService(DogApi);
  const [currentStep, setCurrentStep] = React.useState(stepNumber);
  const { currentLanguage } = useGlobalData();

  const [dogBirthYear, setDogBirthYear] = React.useState(
    new Date().getFullYear()
  );
  const [dogDeathYear, setDogDeathYear] = React.useState(
    new Date().getFullYear()
  );
  const [dogIdValue, setDogIdValue] = React.useState(initialdata?.dogId ?? 0);

  const saveForm = React.useCallback(
    async (formikData) => {
      if (currentStep === 1) {
        await save(formikData);
      }
    },
    [currentStep, save]
  );

  const [dogs, dloading] = useReferential(
    (a) =>
      a.getOwnedDogs(
        user.userId,
        initialdata?.dogId ? initialdata.dogId : 0,
        null,
        null,
        null
      ),
    true,
    []
  );
  const [titles, tloading] = useReferential(
    (a) =>
      a.getBelgianTitles(
        currentLanguage?.languageId,
        dogIdValue,
        initialdata.titleId
      ),
    true,
    []
  );

  const formSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test(
          "dog-deceased",
          t(ETLCodes.DogIsDeceased),
          async function (value) {
            if (
              initialdata?.titleRequestId <= 0 ||
              initialdata?.request?.status === ERequestState.REQUESTED
            ) {
              const res = await dogApi.isDogDeceased(value);
              return !res.value;
            }
            return true;
          }
        )
        .test(
          "is-not-excluded",
          t(ETLCodes.DogIsExcluded),
          async function (value) {
            if (
              initialdata?.titleRequestId <= 0 ||
              initialdata?.request?.status === ERequestState.REQUESTED
            ) {
              const result = await dogApi.isDogExcluded(value);
              return !result.value;
            }
            return true;
          }
        ),
      titleId: Yup.number().nullable().required(t(ETLCodes.Required)),
      year: Yup.string()
        .required(t(ETLCodes.Required))
        .matches(new RegExp("([0-9]){4}"), t(ETLCodes.YearIsNotCorrect))
        .test(
          "year-test",
          t(ETLCodes.YearMustBeBetweenBirthAndDeathError, {
            birthdate:
              dogBirthYear < new Date().getFullYear() - 20
                ? new Date().getFullYear() - 20
                : dogBirthYear,
            deathdate: dogDeathYear ?? new Date().getFullYear(),
          }),
          async function (value) {
            const currentDog = await dogApi.getDog(dogIdValue);
            setDogDeathYear(
              currentDog?.deathDate
                ? currentDog.deathDate.getFullYear()
                : new Date().getFullYear()
            );
            setDogBirthYear(currentDog?.birthDate.getFullYear());
            if (this.parent.dogId && currentDog) {
              if (currentDog.deathDate) {
                return (
                  +value <= currentDog?.deathDate.getFullYear() &&
                  +value >= currentDog?.birthDate.getFullYear()
                );
              } else {
                return (
                  +value >= currentDog?.birthDate.getFullYear() &&
                  +value <= new Date().getFullYear()
                );
              }
            }
          }
        ),
    });
  }, [
    dogApi,
    dogBirthYear,
    dogDeathYear,
    dogIdValue,
    initialdata?.request?.status,
    initialdata?.titleRequestId,
    t,
  ]);

  return (
    <DrupalFormGenerator
      initialValues={initialdata}
      editMode={
        currentStep === 0 &&
        initialdata.request.status === ERequestState.REQUESTED
      }
      onSubmit={() => {}}
      saving={false}
      validationSchema={formSchema}
      loading={loading}
      hideButtons
    >
      {currentStep !== 0 ? (
        <DrupalFieldSet title={t(ETLCodes.RequestResume)} />
      ) : (
        <></>
      )}
      <FGListen field="dogId" onChanged={(value) => setDogIdValue(value)} />
      <FGSelectInput
        name="dogId"
        label={t(ETLCodes.Dog)}
        items={dogs}
        loading={dloading}
      />
      <FGSelectInput
        name="titleId"
        label={t(ETLCodes.Title)}
        items={titles}
        loading={tloading}
      />
      <FGTextInput name="year" label={t(ETLCodes.Year)} maxLength={4} />
      {currentStep === 0 &&
      initialdata.request.status === ERequestState.REQUESTED ? (
        <FGCustomInput label={t(ETLCodes.Diploma)}>
          {(ctx) => (
            <FilePond
              files={files}
              allowMultiple={true}
              onupdatefiles={(f) => {
                ctx.formik.setFieldValue("files", f);
                setFiles(f);
              }}
              labelIdle={t(ETLCodes.FilePondDragOrSelect)}
              disabled={currentStep !== 0}
            />
          )}
        </FGCustomInput>
      ) : null}
      {showStepper ? (
        <FGCustomPanel>
          {(ctx) => {
            return (
              <DrupalStepperButtonBloc
                backOnly={
                  currentStep === 2 ||
                  initialdata?.request?.status !== ERequestState.REQUESTED
                }
                disabled={
                  ctx.formik.values === initialdata || !ctx.formik.isValid
                }
                loading={loading}
                onStepChange={(newStep) => {
                  setCurrentStep(newStep);
                  onStepChange(newStep);
                }}
                stepNumber={currentStep}
                onNextFn={() => saveForm(ctx.formik.values)}
                formik={ctx.formik}
              />
            );
          }}
        </FGCustomPanel>
      ) : null}
    </DrupalFormGenerator>
  );
};
